import { useUpdateOrdersStatus } from "hooks";
import { Order, UpdateOneOrderStatus, UpdateOrderStatusBatchCommandOutput, UpdateOrderStatusCommandOutput } from "@amzn/ito-client";

interface UpdateOneOrderProps extends Omit<UpdateOneOrderStatus, "orderId"> { }

interface UpdateOrderSummaryParams {
  orders: Order[];
  updateAssigneeProps: UpdateOneOrderProps;
  csrfToken: string;
  updateOnAmznHam: boolean;
}

export interface useOrderEditActions {
  updateOrderSummary: (params: UpdateOrderSummaryParams) => void,
  isLoading: boolean,
  data: UpdateOrderStatusBatchCommandOutput | UpdateOrderStatusCommandOutput | undefined,
  error: Error | null,
}

/**
 * A custom hook for managing order status updates in bulk or single order operations.
 * The hook handles special logic for Amazon HAM orders by:
 * - Filtering orders with relations
 * - Mapping orderId to the first relation's ID for HAM orders
 * 
 * @returns {useOrderEditActions} An object containing:
 *   - updateOrderSummary: Function to update order status(es)
 *   - isLoading: Boolean indicating if update operation is in progress
 *   - data: Response data from the update operation
 *   - error: Any error that occurred during the update
 */
export function useUpdateOrder(): useOrderEditActions {
  const { doUpdateOrders, isLoading, data, error } = useUpdateOrdersStatus();
  // update variable name (to should it update amznHam)
  const updateOrderSummary = ({ orders, updateAssigneeProps, csrfToken, updateOnAmznHam }: UpdateOrderSummaryParams) => {
    let actualOrders;
    if(updateOnAmznHam){
      // This mapping anly grabs the first relation from the order.
      // If at some point it changes and an order can have multiple relations,
      // this logic should be changed to make update to more than one relation.
      actualOrders = orders
        .filter((order) => (order?.relations?.length ?? 0) > 0)
        .map((order) => ({
          ...order,
          orderId: order?.relations?.[0].id,
        }));
    } else {
      actualOrders = orders;
    }

    if(actualOrders.length === 0) return;

    doUpdateOrders(actualOrders, updateAssigneeProps, csrfToken, updateOnAmznHam);
  }

  return { updateOrderSummary, isLoading, data, error }
}
